
import { advanceDaily } from './advanceDaily'
import { advanceWeekly } from './advanceWeekly'
import { advanceMonthly } from './advanceMonthly'
import { advanceYearly } from './advanceYearly'

// ============================================================
// LOCAL CALENDAR DATE
// ============================================================

function toLocalDate(value) {
    if (!value) {
        return null
    }

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return null
        }

        return new Date(
            value.getFullYear(),
            value.getMonth(),
            value.getDate()
        )
    }

    if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {
        const [year, month, day] =
            value.split('-').map(Number)

        const result = new Date(year, month - 1, day)

        if (
            result.getFullYear() !== year ||
            result.getMonth() !== month - 1 ||
            result.getDate() !== day
        ) {
            return null
        }

        return result
    }

    const parsed = new Date(value)

    if (Number.isNaN(parsed.getTime())) {
        return null
    }

    return new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate()
    )
}

// ============================================================
// NEXT START DATE FOR A SEASON
// ============================================================
//
// Return the next occurrence of the assigned season's start
// month/day that falls strictly AFTER currentDate.
//
// Examples:
// Summer starts June 21; current date July 1
//   -> June 21 of the following year
//
// Winter starts December 21; current date January 10
//   -> December 21 of the current year
//
// Winter starts December 21; current date December 21
//   -> December 21 of the following year
// ============================================================

function getNextSeasonStartDate({
    currentDate,
    season
}) {
    const current = toLocalDate(currentDate)

    if (!current || !season) {
        return null
    }

    const startMonth = Number(season.start_month)
    const startDay = Number(season.start_day)

    if (
        !Number.isInteger(startMonth) ||
        startMonth < 1 ||
        startMonth > 12 ||
        !Number.isInteger(startDay) ||
        startDay < 1 ||
        startDay > 31
    ) {
        return null
    }

    let candidate = new Date(
        current.getFullYear(),
        startMonth - 1,
        startDay
    )

    // Reject dates that JavaScript silently rolls into
    // the following month, such as February 31.
    if (
        candidate.getMonth() !== startMonth - 1 ||
        candidate.getDate() !== startDay
    ) {
        return null
    }

    // The next season start must be strictly in the future.
    if (candidate <= current) {
        candidate = new Date(
            current.getFullYear() + 1,
            startMonth - 1,
            startDay
        )

        if (
            candidate.getMonth() !== startMonth - 1 ||
            candidate.getDate() !== startDay
        ) {
            return null
        }
    }

    return candidate
}

// ============================================================
// GET NEXT OCCURRENCE DATE
// ============================================================

export function getNextOccurrenceDate({
    template,
    currentDate
}) {
    // Once per Season:
    // recurrence_type_id is NULL and season_id is assigned.
    if (
        template.recurrence_type_id == null &&
        template.season_id
    ) {
        return getNextSeasonStartDate({
            currentDate,
            season: template.seasons
        })
    }

    switch (Number(template.recurrence_type_id)) {
        case 1:
            return advanceDaily({
                template,
                currentDate
            })

        case 2:
            return advanceWeekly({
                template,
                currentDate
            })

        case 3:
            return advanceMonthly({
                template,
                currentDate
            })

        case 4:
            return advanceYearly({
                template,
                currentDate
            })

        // Legacy seasonal records are not supported as a cadence.
        case 5:
            return null

        // Legacy interval records are not supported as a cadence.
        case 6:
            return null

        default:
            return null
    }
}
